import { toLocalDateStr } from "./dateUtils";
// src/utils/calcoloSAL.js
// Motore di calcolo del Computo Metrico / SAL — replica fedele della logica del foglio Excel
// storico: numerazione automatica delle voci, percentuali di avanzamento per ogni periodo di
// SAL, controllo di sforamento oltre il 100%, e il riepilogo finale (avanzamento precedente/
// del periodo/complessivo, importo maturato, fatturato, residuo contrattuale).

// Una riga può essere:
// - "categoria": intestazione di gruppo, grassetto, nessun numero (es. "A) CABINA MT/BT")
// - "voce": una lavorazione vera, con Q.tà/Prezzo/Totale e le percentuali SAL
// - "vuota": riga segnaposto pronta da compilare, non ancora una voce vera
// Il tipo NON si sceglie da un menu — si capisce da solo da quello che c'è scritto,
// esattamente come in Excel: se c'è un titolo marcato in grassetto è categoria, se ci sono
// numeri è voce, altrimenti è ancora vuota/descrittiva.

export function nuovaRigaSAL() {
  return { id: cryptoRandomId(), titolo: false, colore: "", descrizione: "", um: "", quantita: "", prezzoUnitario: "", sal: {} };
}
export function nuovoPeriodoSAL(data) {
  return { id: cryptoRandomId(), data: data || toLocalDateStr(new Date()), stato: "bozza" };
}
function cryptoRandomId() {
  return "r" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export function totaleRiga(riga) {
  const q = parseFloat(riga.quantita)||0, p = parseFloat(riga.prezzoUnitario)||0;
  return q * p;
}

export function totaleSalRigaPeriodo(riga, periodoId) {
  const perc = parseFloat(riga.sal?.[periodoId]?.perc)||0;
  return totaleRiga(riga) * perc/100;
}

// Somma di tutte le percentuali (di tutti i periodi) su questa riga — se supera il 100%
// qualcosa non torna, stesso controllo del foglio Excel originale.
export function percCumulataRiga(riga, periodi) {
  return periodi.reduce((s,per)=>s+(parseFloat(riga.sal?.[per.id]?.perc)||0), 0);
}

// fatturato: con richieste di fatturazione sulla commessa arriva da quelle segnate «Fatturata»
// (utils/fatturazioneCommessa.js), altrimenti è il numero scritto a mano nel SAL.
// richiestoInAttesa: richieste di fatturazione inviate e non ancora fatturate — serve per
// «nonRichiesto», il lavoro maturato che nessuno ha ancora chiesto di fatturare, così lo
// stesso importo non compare due volte (SAL da fatturare + richieste in attesa).
export function calcolaSAL(righe, periodi, fatturato, richiestoInAttesa = 0) {
  const righeVoce = righe.filter(r => totaleRiga(r) > 0);

  // Numerazione automatica: solo le righe con un totale vero vengono numerate, esattamente
  // come la formula COUNT($A$3:A...) del foglio originale.
  let contatore = 0;
  const numerazione = {};
  righe.forEach(r => { if (totaleRiga(r) > 0) { contatore++; numerazione[r.id] = contatore; } });

  const totaleGenerale = righeVoce.reduce((s,r)=>s+totaleRiga(r), 0);

  const perPeriodo = periodi.map(per => {
    const totale = righeVoce.reduce((s,r)=>s+totaleSalRigaPeriodo(r,per.id), 0);
    const percSulTotale = totaleGenerale>0 ? (totale/totaleGenerale*100) : 0;
    return { periodo: per, totale, percSulTotale };
  });

  // Cumulativo progressivo: quanto si era arrivati FINO a un certo periodo incluso
  let cumulativo = 0;
  const cumulativi = perPeriodo.map(p => { cumulativo += p.percSulTotale; return cumulativo; });

  const ultimo = perPeriodo.length - 1;
  const avanzamentoPrecedente = ultimo > 0 ? cumulativi[ultimo-1] : 0;
  const avanzamentoDelPeriodo = ultimo >= 0 ? perPeriodo[ultimo].percSulTotale : 0;
  const avanzamentoComplessivo = ultimo >= 0 ? cumulativi[ultimo] : 0;

  const salMaturato = perPeriodo.reduce((s,p)=>s+p.totale, 0);
  const fatt = parseFloat(fatturato)||0;
  const daFatturare = salMaturato - fatt;
  const inAttesa = Math.max(0, parseFloat(richiestoInAttesa) || 0);
  const nonRichiesto = Math.max(0, daFatturare - inAttesa);
  const residuoContrattuale = totaleGenerale - salMaturato;

  // Righe che sforano il 100% tra tutti i periodi sommati — stesso avviso del foglio originale
  const righeSforate = righeVoce.filter(r => percCumulataRiga(r, periodi) > 100);

  return {
    numerazione, totaleGenerale, perPeriodo, cumulativi,
    avanzamentoPrecedente, avanzamentoDelPeriodo, avanzamentoComplessivo,
    salMaturato, fatturato: fatt, daFatturare, richiestoInAttesa: inAttesa, nonRichiesto, residuoContrattuale, righeSforate,
  };
}

/** calcolaSAL di un documento computiSAL, con fatturato e richieste in attesa già riportati. */
export function calcolaSALComputo(s) {
  return calcolaSAL(s?.righe || [], s?.periodi || [], s?.fatturato || 0, s?.richiestoInAttesa || 0);
}

// Proiezione della data di completamento, dal ritmo medio reale tenuto tra il primo e
// l'ultimo SAL salvato — non una promessa, una previsione statistica che si aggiorna da
// sola a ogni nuovo SAL. Richiede almeno due SAL salvati: con uno solo non c'è ancora un
// ritmo da misurare, e la funzione restituisce null piuttosto che inventare un numero.
export function previsioneCompletamento(periodi, cumulativi) {
  const indiciSalvati = periodi.map((p,i)=>({p,i})).filter(({p})=>p.stato==="salvato");
  if (indiciSalvati.length < 2) return null;

  const primo = indiciSalvati[0];
  const ultimo = indiciSalvati[indiciSalvati.length-1];
  const percPrimo = cumulativi[primo.i]||0;
  const percUltimo = cumulativi[ultimo.i]||0;
  if (percUltimo >= 100) return { completato: true };

  const giorniTrascorsi = (new Date(ultimo.p.data+"T00:00:00") - new Date(primo.p.data+"T00:00:00")) / 86400000;
  const percAvanzata = percUltimo - percPrimo;
  if (giorniTrascorsi <= 0 || percAvanzata <= 0) return { fermo: true }; // nessun avanzamento misurabile nel periodo osservato

  const ritmoGiornaliero = percAvanzata / giorniTrascorsi;
  const percRimanente = 100 - percUltimo;
  const giorniStimati = Math.ceil(percRimanente / ritmoGiornaliero);
  const dataStimata = new Date(ultimo.p.data+"T00:00:00");
  dataStimata.setDate(dataStimata.getDate() + giorniStimati);

  return { completato:false, fermo:false, dataStimata, ritmoGiornaliero, giorniStimati, nSalDiOsservazione: indiciSalvati.length };
}

// Trasforma **testo** in grassetto — usato sia nell'anteprima a schermo che nel PDF.
// Scelta deliberata al posto di un editor di testo formattato completo: stessa resa visiva,
// senza il rischio di un componente molto più complesso e fragile.
export function testoConGrassetto(testo) {
  if (!testo) return "";
  const parti = String(testo).split(/(\*\*[^*]+\*\*)/g);
  return parti.map((p,i) => {
    if (p.startsWith("**") && p.endsWith("**")) return { bold:true, testo:p.slice(2,-2), key:i };
    return { bold:false, testo:p, key:i };
  });
}
